import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {Divider, Grid} from 'antd'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {TitleWithStatus} from '@/components/composite'
import {ModalContainer} from '@/components/containers'
import {PayByLinkOverviewModalContainerStoreSymbol} from '@/pages/PayByLink'
import {DateTimeLabel, Description, MobileBackButton, ModalFormFooter, Text} from '@/components'
import {
    getPayByLinkPaymentStatusIconType,
    getPayByLinkPaymentStatusTagType
} from '~/code/pages/PayByLink/services'
import {getAmountWithCurrentCurrency} from '@/utils'
import {TOverviewModalStore} from './TOverviewModalStore'
import {OverviewModalProps} from './props'

export const OverviewModal: React.FC<OverviewModalProps> = observer(
    ({injectableIdentifier, paymentLink}: OverviewModalProps) => {
        const {t} = useTranslation()
        const screens = Grid.useBreakpoint()
        const isMobile = screens.xs
        const overViewModalStore = useInjection<TOverviewModalStore>(
            injectableIdentifier || PayByLinkOverviewModalContainerStoreSymbol
        )

        useEffect(() => {
            overViewModalStore.setPaymentLink?.(paymentLink)
        }, [overViewModalStore, paymentLink])

        return (
            <ModalContainer
                title={t('OverviewModal.paymentView')}
                injectableIdentifier={injectableIdentifier}
                footer={
                    <ModalFormFooter
                        onCancel={overViewModalStore.onCancel}
                        onOk={overViewModalStore.onOk}
                        cancelText={t('OverviewModal.cancel')}
                        okText={t('OverviewModal.ok')}
                    />
                }
            >
                {isMobile && <MobileBackButton onCancel={overViewModalStore.onCancel} />}

                <TitleWithStatus
                    subtitle={paymentLink.description}
                    statusProps={{
                        status: t(`PayByLink.statuses.${paymentLink.status}`, { defaultValue: paymentLink.status }),
                        type: getPayByLinkPaymentStatusTagType(paymentLink.status),
                        iconType: getPayByLinkPaymentStatusIconType(paymentLink.status)
                    }}
                >
                    <Text>{getAmountWithCurrentCurrency(paymentLink.amount)}</Text>
                </TitleWithStatus>

                <Divider type={'horizontal'} />

                <Description
                    dataSource={[
                        {
                            label: t('OverviewModal.date'),
                            value: paymentLink.description,
                            key: t('OverviewModal.date'),
                            render: () => <DateTimeLabel date={paymentLink.createdDate} />
                        },
                        {
                            label: t('OverviewModal.orderNumber'),
                            value: paymentLink.invoiceId,
                            key: t('OverviewModal.orderNumber')
                        },
                        {
                            label: t('OverviewModal.customer'),
                            value: paymentLink.customerName,
                            key: t('OverviewModal.customer')
                        },
                        {
                            label: t('OverviewModal.transactionType'),
                            value: t('OverviewModal.paymentLink'),
                            key: t('OverviewModal.transactionType')
                        }
                    ]}
                />
            </ModalContainer>
        )
    }
)
